import { deviceAdminApi } from '@/api';
import { Popover } from '@/components/common';
import { handleResponse } from '@/Helper';
import { RoleTab } from '@/model/DeviceAdminModel';
import { Logger } from '@/model/LoggingModel';
import { DigiTracPermission, DigiTracPermissionItem, HardwarePermission } from '@/model/RolesModel';
import { Radio, RadioChangeEvent, Spin } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { batch } from 'react-redux';
import { TimeZone } from '.';
import { CloneFromRole } from '../CloneFromRole/CloneFromRole';
import { buildRadioOptionsByEnum, getDefaultDigiTracPermission } from '../helper';
import { setForceRenderContextModelAction, setHardwarePermissionsAction } from '../RoleContext/action';
import { RoleStore } from '../RoleContext/context';
import styles from './hardwarePermissions.module.scss';
import { NodeTreeView } from './NodeTreeView/NodeTreeView';
import { isDeepChildren } from './SearchHardware/SearchHardware';

const { Group: RadioGroup } = Radio;

const defaultModifiedObject: DigiTracPermission = getDefaultDigiTracPermission();

export enum HardwareView {
	//Same data set
	CardView,
	HierarchyView,
	//Same data set
	GroupView,
	ListView,
}

type Props = {};

const HardwarePermissions: React.FC<Props> = () => {
	//#region state management variables
	const {
		roleContextInitialState: {
			roles,
			props: { roleId },
			hardwarePermissions,
		},
		dispatcher,
	} = useContext(RoleStore);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [fakeRender, setFakeRender] = useState<boolean>(true);
	const [hardwareView, setHardwareView] = useState<HardwareView>(HardwareView.HierarchyView);

	const availableHardwarePermissionsRef = useRef<DigiTracPermission>(undefined);
	const setAvailableHardwarePermissionsRef = (newAvailableHardwarePermissionsRef: DigiTracPermission): void => {
		availableHardwarePermissionsRef.current = newAvailableHardwarePermissionsRef;
	};
	const getAvailableHardwarePermissionsRef = (): DigiTracPermission => {
		return availableHardwarePermissionsRef.current;
	};

	const modifiedHardwarePermissionsRef = useRef<Record<number, DigiTracPermissionItem>>(defaultModifiedObject.Permissions);
	const setModifiedHardwarePermissionsRef = (newModifiedHardwarePermissionsRef: Record<number, DigiTracPermissionItem>): void => {
		modifiedHardwarePermissionsRef.current = newModifiedHardwarePermissionsRef;
	};
	const getModifiedHardwarePermissionsRef = (): Record<number, DigiTracPermissionItem> => {
		return modifiedHardwarePermissionsRef.current;
	};
	//#endregion

	useEffect(() => {
		deviceAdminApi
			.getHardwarePermission(roleId)
			.then(response => {
				if (!handleResponse(response)) {
					const { Entity } = response;
					batch(() => {
						setAvailableHardwarePermissionsRef(Entity);
						setFakeRender(prevState => !prevState);
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			})
			.catch(err => {
				Logger.writeErrorLog(`${err.name}: ${err.message}`);
			});
	}, []);

	const handleOnModifiedPermissions = (modifiedPermissions: HardwarePermission[]) => {
		const availableHardwarePermissions = getAvailableHardwarePermissionsRef();
		const modifiedHardwarePermissions = getModifiedHardwarePermissionsRef();

		let counter = 0;
		modifiedPermissions.forEach(({ ObjectType, ObjectId, ParentId, HasPermission }) => {
			const available: DigiTracPermissionItem = availableHardwarePermissions.Permissions[ObjectType];
			if (HasPermission) {
				counter++;
				available.Items[ObjectId] = {
					ObjectId,
					ObjectType,
					HasPermission,
				};
			} else {
				counter--;
				delete available.Items[ObjectId];
			}

			const modified: DigiTracPermissionItem = modifiedHardwarePermissions[ObjectType];
			modified.Items[ObjectId] = {
				ObjectId,
				ObjectType,
				HasPermission,
			};

			if (isDeepChildren(ObjectType)) {
				if (!HasPermission) {
					availableHardwarePermissions.ControllerFullSelected = availableHardwarePermissions.ControllerFullSelected.filter(
						x => x.ObjectId !== ParentId
					);
				}
			}
		});
		availableHardwarePermissions.TotalSelectedItems += counter;

		batch(() => {
			setAvailableHardwarePermissionsRef(availableHardwarePermissions);
			setModifiedHardwarePermissionsRef(modifiedHardwarePermissions);
			setFakeRender(prevState => !prevState);
			dispatcher(
				setHardwarePermissionsAction({
					...hardwarePermissions,
					isDirty: true,
					modifiedHardwarePermissions: modifiedHardwarePermissions,
				})
			);
		});
	};

	const handleOnAppendPermissions = (extraPermissions: HardwarePermission[]) => {
		const availableHardwarePermissions = getAvailableHardwarePermissionsRef();
		extraPermissions.forEach(permission => {
			const available: DigiTracPermissionItem = availableHardwarePermissions.Permissions[permission.ObjectType];
			available.Items[permission.ObjectId] = permission;
		});
		setAvailableHardwarePermissionsRef(availableHardwarePermissions);
		setFakeRender(prevState => !prevState);
	};

	const handleOnChangeHardwareView = (e: RadioChangeEvent) => {
		setHardwareView(e.target.value);
	};

	const getData = (): DigiTracPermission => {
		return getAvailableHardwarePermissionsRef();
	};

	const setData = (
		isDirty: boolean,
		data: { roleId: number; modifiedHardwarePermissions: Record<number, DigiTracPermissionItem>; availableHardwarePermissions: DigiTracPermission }
	) => {
		const { roleId, modifiedHardwarePermissions, availableHardwarePermissions } = data;
		const userRole = roles.find(d => d.Id === Number(roleId));

		batch(() => {
			setAvailableHardwarePermissionsRef(availableHardwarePermissions);
			setModifiedHardwarePermissionsRef(modifiedHardwarePermissions);
			setFakeRender(prevState => !prevState);
			dispatcher(setForceRenderContextModelAction());
			dispatcher(
				setHardwarePermissionsAction({
					modifiedHardwarePermissions: modifiedHardwarePermissions,
					globalTimeZoneId: userRole.GlobalTimeZoneId,
					isDirty: isDirty,
				})
			);
		});
	};

	const data = getAvailableHardwarePermissionsRef();
	const selectedItemsInRoles = data?.TotalSelectedItems ?? 0;

	let view: React.ReactNode = <></>;
	switch (hardwareView) {
		case HardwareView.CardView:
		case HardwareView.HierarchyView:
			view = (
				<NodeTreeView
					hardwareView={hardwareView}
					availableHardwarePermissions={data}
					handleOnAppendPermissions={handleOnAppendPermissions}
					handleOnModifiedPermissions={handleOnModifiedPermissions}
				/>
			);
			break;
	}

	return (
		<div className={styles.hardwareContainer}>
			<CloneFromRole setLoading={setIsLoading} tab={RoleTab.HardwarePermission} getData={getData} setData={setData} />
			<Spin size='large' tip={`${_('Loading')}...`} spinning={isLoading}>
				<div>
					{false && (
						<Popover title={_(HardwareView[hardwareView])} isActive={true}>
							<RadioGroup onChange={handleOnChangeHardwareView} value={hardwareView}>
								{buildRadioOptionsByEnum('vertical', HardwareView)}
							</RadioGroup>
						</Popover>
					)}
					<TimeZone selectedItemsInRoles={selectedItemsInRoles} />
				</div>
				{isLoading ? <div className={styles.spinPlaceHolder} /> : view}
			</Spin>
		</div>
	);
};

export { HardwarePermissions };
